import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Car } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ApplyFahrschulePresetRequest } from "../../features/organization/contracts/ApplyFahrschulePresetRequest";
import { ApplyPresetResponse } from "../../features/organization/contracts/ApplyPresetResponse";
import { TheoryEveningRequest } from "../../features/organization/contracts/TheoryEveningRequest";
import { selectPresetStatus } from "../../features/organization/organization-slice";
import { applyFahrschulePreset } from "../../features/organization/slice-extensions/organization-preset-slice-extension";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import {
  DEFAULT_CAPACITY,
  LICENCE_CLASSES,
  defaultEvenings,
  firstTheoryDate,
  nextMonday,
} from "./fahrschulePresetDefaults";
import TheoryEveningRows from "./TheoryEveningRows";
import PresetResultPanel from "./PresetResultPanel";

interface FahrschulePresetFormProps {
  /** Called after every successful run, issues or not, with the first theory date ("YYYY-MM-DD"). */
  onApplied?: (result: ApplyPresetResponse, firstTheoryDate: string) => void;
}

/**
 * The Fahrschule preset: licence classes (B preselected), theory evenings (Tuesday and
 * Thursday 18:30), capacity and start date. Shared by /welcome/kind and the
 * organization settings card; applying again only adds what is missing.
 */
const FahrschulePresetForm: React.FC<FahrschulePresetFormProps> = ({ onApplied }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const busy = useAppSelector(selectPresetStatus) === LoadingStatus.Loading;
  const [classes, setClasses] = useState<string[]>(["B"]);
  const [evenings, setEvenings] = useState<TheoryEveningRequest[]>(defaultEvenings);
  const [capacity, setCapacity] = useState<string>(String(DEFAULT_CAPACITY));
  const [startDate, setStartDate] = useState<string>(() => nextMonday());
  const [result, setResult] = useState<ApplyPresetResponse | null>(null);
  const [lastRequest, setLastRequest] = useState<ApplyFahrschulePresetRequest | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggleClass = (code: string) =>
    setClasses((current) => (current.includes(code) ? current.filter((c) => c !== code) : [...current, code]));

  const submit = async (request: ApplyFahrschulePresetRequest) => {
    setError(null);
    setLastRequest(request);
    try {
      const applied = await dispatch(applyFahrschulePreset(request)).unwrap();
      setResult(applied);
      onApplied?.(applied, firstTheoryDate(request.startDate, request.theoryEvenings));
    } catch (rejected) {
      setError((rejected as { message?: string })?.message ?? t("preset.fahrschule.failed"));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (classes.length === 0) {
      setError(t("preset.fahrschule.classesRequired"));
      return;
    }
    const seats = Number.parseInt(capacity, 10);
    submit({
      licenceClasses: LICENCE_CLASSES.filter((code) => classes.includes(code)),
      theoryEvenings: evenings,
      theoryCapacity: Number.isFinite(seats) ? seats : null,
      startDate,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <fieldset>
        <legend className="mb-1 block text-sm font-medium text-grey-30 dark:text-white">
          {t("preset.fahrschule.classes")}
        </legend>
        <div className="flex flex-wrap gap-2">
          {LICENCE_CLASSES.map((code) => (
            <label
              key={code}
              className="flex cursor-pointer items-center gap-2 rounded-xl border border-grey-90 px-3 py-2 text-sm text-grey-20 dark:border-primary-20 dark:text-white"
            >
              <input
                type="checkbox"
                checked={classes.includes(code)}
                disabled={busy}
                onChange={() => toggleClass(code)}
              />
              <span>{code}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <TheoryEveningRows evenings={evenings} disabled={busy} onChange={setEvenings} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm text-grey-30 dark:text-grey-70">{t("preset.fahrschule.capacity")}</span>
          <input
            type="number"
            min={1}
            max={60}
            className="input mt-1 w-full dark:[color-scheme:dark]"
            value={capacity}
            disabled={busy}
            onChange={(e) => setCapacity(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-sm text-grey-30 dark:text-grey-70">{t("preset.fahrschule.startDate")}</span>
          <input
            type="date"
            className="input mt-1 w-full dark:[color-scheme:dark]"
            value={startDate}
            disabled={busy}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
      </div>

      <button type="submit" className="btn btn-primary" disabled={busy}>
        <Car size={18} />
        <span>{busy ? t("preset.fahrschule.applying") : t("preset.fahrschule.apply")}</span>
      </button>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {result && lastRequest && (
        <PresetResultPanel
          result={result}
          busy={busy}
          calendarDate={firstTheoryDate(lastRequest.startDate, lastRequest.theoryEvenings)}
          onTryAgain={() => submit(lastRequest)}
        />
      )}
    </form>
  );
};

export default FahrschulePresetForm;
